class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // Save opener only when opening
        if (!this.isOpen) this.rememberOpener(e);

        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  /* Cart speedup (Sep 2026): only these regions change when the cart updates,
     so we patch them in place instead of rebuilding the whole drawer. The
     upsell carousel and the Shiprocket checkout block stay untouched, which
     keeps updates fast and stops the drawer from flickering/re-initialising. */
  getSurgicalSelectors() {
    return [
      "#CartDrawer-Form",
      ".wt-free-shipping-bar",
      "#CartDrawer-TotalQty",
      ".ultra-track",
      ".ultra-steps",
      ".ultra-sub",
      ".ultra-progress",
      ".wt-cart__subtotal",
      ".wt-cart__subtitle__text",
    ];
  }

  renderContents(parsedState, isClosedCart = true) {
    const sections = parsedState.sections || {};

    const bubbleHtml = sections["cart-icon-bubble"];
    if (bubbleHtml) {
      const bubble = document.getElementById("cart-icon-bubble");
      if (bubble) {
        const html = this.getSectionInnerHTML(bubbleHtml, ".shopify-section");
        if (html) bubble.innerHTML = html;
      }
    }

    const drawerHtml = sections["cart-drawer"];
    if (drawerHtml) {
      const sourceDoc = new DOMParser().parseFromString(
        drawerHtml,
        "text/html",
      );
      const sourceForm = sourceDoc.querySelector("#CartDrawer-Form");
      const targetForm = document.querySelector("#CartDrawer-Form");
      /* If the page is missing a region the server HTML has (e.g. the footer
         after an empty-cart state), the structures differ - fall back to a
         full replace so nothing is lost. */
      const structureMismatch = this.getSurgicalSelectors().some((sel) => {
        return sourceDoc.querySelector(sel) && !document.querySelector(sel);
      });
      if (sourceForm && targetForm && !structureMismatch) {
        // Surgical update: patch only the regions that change.
        this.getSurgicalSelectors().forEach((sel) => {
          const src = sourceDoc.querySelector(sel);
          const tgt =
            document.querySelector("#CartDrawer " + sel) ||
            document.querySelector(sel);
          if (src && tgt) tgt.innerHTML = src.innerHTML;
        });
      } else {
        // Fallback (e.g. empty-cart markup): full replace as before.
        const cartDrawerEl = document.getElementById("CartDrawer");
        const src = sourceDoc.querySelector("#CartDrawer");
        if (cartDrawerEl && src) cartDrawerEl.innerHTML = src.innerHTML;
      }
    }

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const source = html.querySelector("cart-drawer-items");
        if (source) {
          this.innerHTML = source.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /* Cart speedup (Sep 2026): replace only the changed regions of the drawer
     instead of the whole .drawer__inner. See CartDrawerSection above. */
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: "#CartDrawer-Form" },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".wt-free-shipping-bar",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: "#CartDrawer-TotalQty",
      },
      { id: "CartDrawer", section: "cart-drawer", selector: ".ultra-track" },
      { id: "CartDrawer", section: "cart-drawer", selector: ".ultra-steps" },
      { id: "CartDrawer", section: "cart-drawer", selector: ".ultra-sub" },
      { id: "CartDrawer", section: "cart-drawer", selector: ".ultra-progress" },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".wt-cart__subtotal",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".wt-cart__subtitle__text",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
